import type { FleetCellPlanView } from '@/shared/api/__generated__'
import type { StatusTone } from '@/shared/ui/status-badge'

/**
 * На каком шаге стоит слив ячейки. Бэк отдаёт `pending-calls` на ДВА разных
 * ожидания, и различает их только `note`:
 *
 *  - «живых звонков: N» — ждём абонента: разговоры дорабатывают, снос
 *    отложен до тишины;
 *  - «неподтверждённых отчётов: N» — ждём брокера: звонки кончились, но
 *    отчёты за них ещё не доехали. Минуты считаются ТОЛЬКО по `call.result`,
 *    поэтому это деньги за конец кампании в пути, а не техническая мелочь.
 *
 * Второе ожидание конечно: вышел грейс — юнит сносится, а отчёты теряются.
 * Поэтому шаг обязан быть виден ДО потери, а не после.
 */
export type ReleaseStepKind =
  | 'pending-calls'
  | 'pending-reports'
  | 'waiting'
  | 'dismantled'
  | 'paused'
  | 'not-found'
  | 'unknown'

export interface ReleaseStep {
  cellId: string
  kind: ReleaseStepKind
  /** Сколько именно ждём — звонков или отчётов; 0, когда числа в ответе нет. */
  count: number
  label: string
  detail: string
  tone: StatusTone
  /** Слив ещё не закончен: следующий тик подвинет ячейку дальше. */
  waiting: boolean
  /** Ожидание стоит денег и грозит потерей — оператору стоит вмешаться. */
  atRisk: boolean
}

const CALLS = /живых\s+звонков:\s*(\d+)/i
const REPORTS = /неподтверждённых\s+отч[её]тов:\s*(\d+)/i

const count = (note: string, re: RegExp): number => {
  const hit = re.exec(note)
  return hit?.[1] ? Number(hit[1]) : 0
}

export function releaseStep(plan: FleetCellPlanView): ReleaseStep {
  const cellId = plan.cell_id
  const note = plan.note ?? ''

  if (plan.state === 'pending-calls') {
    const reports = count(note, REPORTS)
    if (reports > 0 || REPORTS.test(note)) {
      return {
        cellId,
        kind: 'pending-reports',
        count: reports,
        label: 'Ждём брокера',
        detail: `Разговоров нет, но отчёты за последние звонки ещё не подтверждены: ${reports}. Минуты считаются только по ним — это выручка конца кампании в пути. Грейс конечен: когда он выйдет, юнит снесут, а отчёты потеряются.`,
        tone: 'danger',
        waiting: true,
        atRisk: true,
      }
    }
    const calls = count(note, CALLS)
    if (calls > 0 || CALLS.test(note)) {
      return {
        cellId,
        kind: 'pending-calls',
        count: calls,
        label: 'Ждём абонентов',
        detail: `Живых звонков: ${calls}. Ячейка новых заданий не берёт, снос отложен до тишины — обрывать разговор ради экономии минуты железа нельзя.`,
        tone: 'warn',
        waiting: true,
        atRisk: false,
      }
    }
    return {
      cellId,
      kind: 'waiting',
      count: 0,
      label: 'Ячейка не в тишине',
      detail: note || 'Снос отложен: ядро назвало шаг без подробностей.',
      tone: 'warn',
      waiting: true,
      atRisk: false,
    }
  }

  if (plan.state === 'dismantled') {
    return {
      cellId,
      kind: 'dismantled',
      count: 0,
      label: 'Снесена',
      detail: 'Серверы удалены у провайдера, аренды освобождены, строка ячейки стёрта.',
      tone: 'ok',
      waiting: false,
      atRisk: false,
    }
  }

  if (plan.state === 'paused') {
    return {
      cellId,
      kind: 'paused',
      count: 0,
      label: 'Запаузена',
      detail:
        'Юниты остановлены у провайдера с подтверждённой семантикой frees-gpu — карта освобождена, строка юнита жива.',
      tone: 'info',
      waiting: false,
      atRisk: false,
    }
  }

  if (plan.state === 'not-found') {
    return {
      cellId,
      kind: 'not-found',
      count: 0,
      label: 'Ячейки нет',
      detail: note || 'Ячейку уже снесли — сносить нечего.',
      tone: 'muted',
      waiting: false,
      atRisk: false,
    }
  }

  return {
    cellId,
    kind: 'unknown',
    count: 0,
    label: plan.state || '—',
    detail: note || 'Шаг слива админке незнаком — словарь отстал от ядра.',
    tone: 'muted',
    waiting: true,
    atRisk: false,
  }
}

export function releaseSteps(plans: FleetCellPlanView[] | null | undefined): ReleaseStep[] {
  return (plans ?? []).map(releaseStep)
}
